
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Header = () => {
  const { t, i18n } = useTranslation('common');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const location = useLocation();

  // Fallback navigation data
  const fallbackNavigation = [
    { name: 'Home', href: '/' },
    { name: 'Services', href: '/services' },
    { name: 'Portfolio', href: '/portfolio' },
    { name: 'About', href: '/about' },
    { name: 'Contact', href: '/contact' }
  ];

  const fallbackLanguages = [
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'pt', name: 'Português', flag: '🇵🇹' },
    { code: 'ro', name: 'Română', flag: '🇷🇴' }
  ];

  const fallbackContact = {
    phone: '+41 77 218 45 02',
    email: 'info@alpbuild.ch',
    address: 'Switzerland, Vaud and Geneva cantons',
    whatsapp: '+41772184502',
    workingHours: 'Mon-Fri: 7:00-17:00'
  };

  // Get navigation data with fallback
  let navigation;
  try {
    const navData = t('navigation', { returnObjects: true });
    navigation = Array.isArray(navData) ? navData : fallbackNavigation;
  } catch (error) {
    navigation = fallbackNavigation;
  }

  // Get languages data with fallback
  let languages;
  try {
    const languagesData = t('languages', { returnObjects: true });
    languages = Array.isArray(languagesData) ? languagesData : fallbackLanguages;
  } catch (error) {
    languages = fallbackLanguages;
  }

  // Get contact data with fallback
  let contact;
  try {
    const contactData = t('contact', { returnObjects: true });
    contact = contactData && typeof contactData === 'object' ? contactData : fallbackContact;
  } catch (error) {
    contact = fallbackContact;
  }

  const changeLanguage = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setIsLanguageOpen(false);
  };

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  return (
    <header className="bg-white shadow-sm sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo and Company Name */}
          <Link to="/" className="flex items-center space-x-4">
            <img 
              src="https://static.readdy.ai/image/42d13efc32449c379a28fcec32248951/43e47bc0135f16267a464c8a3f4fef40.jpeg" 
              alt="Alpbuild.ch Logo" 
              className="h-16 w-auto drop-shadow-lg"
            />
            <div className="text-2xl md:text-3xl font-bold bg-gradient-to-br from-amber-700 via-green-600 to-yellow-600 bg-clip-text text-transparent drop-shadow-lg" 
                 style={{ 
                   fontFamily: '"Inter", "Helvetica Neue", sans-serif',
                   letterSpacing: '0.1em',
                   textShadow: '2px 2px 4px rgba(0,0,0,0.15), 1px 1px 2px rgba(139,69,19,0.3)',
                   filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.1))'
                 }}>
              Alpbuild.ch
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navigation && navigation.map((item, index) => (
              <Link
                key={item.name || index}
                to={item.href || '/'}
                className={`text-sm font-medium transition-colors hover:text-blue-600 whitespace-nowrap cursor-pointer ${
                  location.pathname === item.href
                    ? 'text-blue-600'
                    : 'text-gray-700'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Right Side - Language Selector & Contact */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Language Selector */}
            {languages && languages.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                  className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <span>{currentLanguage.flag}</span>
                  <span>{currentLanguage.name}</span>
                  <i className={`ri-arrow-down-s-line transition-transform ${isLanguageOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {isLanguageOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                    {languages.map((language, index) => (
                      <button
                        key={language.code || index}
                        onClick={() => changeLanguage(language.code)}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center space-x-3 cursor-pointer ${
                          i18n.language === language.code ? 'text-blue-600 bg-blue-50' : 'text-gray-700'
                        }`}
                      >
                        <span>{language.flag}</span>
                        <span>{language.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Contact Button */}
            {contact && contact.phone && (
              <a
                href="tel:+41772184502"
                className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>
                {contact.phone}
              </a>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 cursor-pointer"
          >
            <i className={`text-xl ${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-gray-200 py-4">
            <nav className="space-y-2">
              {navigation && navigation.map((item, index) => (
                <Link
                  key={item.name || index}
                  to={item.href || '/'}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block px-3 py-2 text-base font-medium rounded-lg transition-colors cursor-pointer ${
                    location.pathname === item.href
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>

            {/* Mobile Language Selector */}
            {languages && languages.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="px-3 py-2 text-sm font-medium text-gray-500 mb-2">Language</div>
                <div className="grid grid-cols-2 gap-2">
                  {languages.map((language, index) => (
                    <button
                      key={language.code || index}
                      onClick={() => changeLanguage(language.code)}
                      className={`flex items-center space-x-2 px-3 py-2 text-sm rounded-lg transition-colors cursor-pointer ${
                        i18n.language === language.code
                          ? 'text-blue-600 bg-blue-50'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span>{language.flag}</span>
                      <span>{language.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Mobile Contact */}
            {contact && contact.phone && (
              <div className="mt-4 pt-4 border-top border-gray-200">
                <a
                  href="tel:+41772184502"
                  className="block w-full bg-blue-600 text-white px-4 py-3 rounded-lg text-center font-medium hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line mr-2"></i>
                  {contact.phone}
                </a>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Overlay for language dropdown */}
      {isLanguageOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsLanguageOpen(false)}
        />
      )}
    </header>
  );
};

export default Header;
